import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { formatMoscowDateTime, giveawayChannelUrl, giveawayEntrySchema, giveawayLimits, giveawayNetworkLabels, giveawayNetworks, sanitizeGiveawaySource } from '../../shared/giveaway';
import type { GiveawayAlreadyEntered, GiveawayEntryResponse, GiveawayNetwork, GiveawayPublic, GiveawayPublicNetwork } from '../../shared/giveaway';
import type { ApiError } from '../../shared/schemas';
import { firstTouchRef } from '../analytics/attribution';
import { trackGoal } from '../analytics/tracker';
import { apiErrorMessage } from '../apiError';
import { InitialDataContext } from '../initialData';
import './booking.css';
import './contest.css';

/** Opens the contest popup; `null` while the contest does not accept entries. */
const ContestContext = createContext<(() => void) | null>(null);

export function useContest() {
  return useContext(ContestContext);
}

/** Last accepted entry of this browser, so reopening the popup goes straight to «Осталось подписаться». */
const storedEntryKey = 'iz_contest_entry';
const announceLater = 'дату объявим в Telegram-канале';
const socialPlaceholders: Record<GiveawayNetwork, string> = { telegram: '@username или t.me/username', max: 'ссылка max.ru/… или ник' };

function readStoredEntry(): GiveawayEntryResponse | null {
  try {
    const value = JSON.parse(window.localStorage.getItem(storedEntryKey) ?? 'null') as Partial<GiveawayEntryResponse> | null;
    // Rendered as links: accept only the shape the server returns.
    if (!value || typeof value.id !== 'number' || !giveawayNetworks.includes(value.network as GiveawayNetwork)
      || typeof value.botUrl !== 'string' || !value.botUrl.startsWith('https://')
      || typeof value.channelUrl !== 'string' || !value.channelUrl.startsWith('https://')) return null;
    return { id: value.id, network: value.network as GiveawayNetwork, botUrl: value.botUrl, channelUrl: value.channelUrl };
  } catch { return null; }
}

function writeStoredEntry(entry: GiveawayEntryResponse | null) {
  try {
    if (entry) window.localStorage.setItem(storedEntryKey, JSON.stringify(entry));
    else window.localStorage.removeItem(storedEntryKey);
  } catch { /* storage unavailable: step 2 just won't survive a reload */ }
}

/** Mounted once in the public layout: the contest popup shared by the floating burst and /rozygrysh. */
export function ContestProvider({ children }: { children: ReactNode }) {
  const giveaway = useContext(InitialDataContext).giveaway;
  const [isOpen, setIsOpen] = useState(false);
  // The content stays mounted after the first opening, so an accidental close keeps the typed form.
  const [hasOpened, setHasOpened] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const location = useLocation();

  const openContest = useCallback(() => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setHasOpened(true);
    setIsOpen(true);
  }, []);
  const closeContest = useCallback(() => setIsOpen(false), []);

  useLayoutEffect(() => { setIsOpen(false); }, [location.key]);
  useEffect(() => {
    const element = dialog.current;
    if (!isOpen || !element) return;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = opener.current;
    element.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      element.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [isOpen]);

  if (!giveaway?.entriesOpen) return <ContestContext.Provider value={null}>{children}</ContestContext.Provider>;

  function trapFocus(event: KeyboardEvent<HTMLDialogElement>) {
    if (event.key !== 'Tab') return;
    const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled):not([tabindex="-1"]), select:not(:disabled), textarea:not(:disabled), a[href]')].filter((element) => element.getClientRects().length > 0);
    const first = controls[0];
    const last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  }

  function closeOnBackdrop(event: MouseEvent<HTMLDialogElement>) {
    if (event.target !== event.currentTarget) return;
    const box = event.currentTarget.getBoundingClientRect();
    if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeContest();
  }

  return <ContestContext.Provider value={openContest}>
    {children}
    <dialog ref={dialog} className="booking-dialog contest-dialog" aria-labelledby="contest-title" onCancel={(event) => { event.preventDefault(); closeContest(); }} onKeyDown={trapFocus} onClick={closeOnBackdrop}>
      <div className="booking-dialog-heading"><h2 id="contest-title">Конкурс</h2><button type="button" className="booking-close" aria-label="Закрыть конкурс" onClick={closeContest}>×</button></div>
      {hasOpened && <ContestContent giveaway={giveaway} />}
    </dialog>
  </ContestContext.Provider>;
}

function ContestContent({ giveaway }: { giveaway: GiveawayPublic }) {
  const [entry, setEntry] = useState<GiveawayEntryResponse | null>(readStoredEntry);

  function entered(response: GiveawayEntryResponse) {
    writeStoredEntry(response);
    setEntry(response);
  }
  function startOver() {
    writeStoredEntry(null);
    setEntry(null);
  }

  return <div className="contest-body">
    <section className="contest-info" aria-label="О конкурсе">
      <p className="contest-info-title">{giveaway.title}</p>
      <p className="contest-info-prize"><strong>Приз:</strong> {giveaway.prizeText}</p>
      {giveaway.description && <p className="contest-info-description">{giveaway.description}</p>}
      <dl className="contest-info-dates">
        <div><dt>Приём заявок до</dt><dd>{giveaway.endsAt ? formatMoscowDateTime(giveaway.endsAt) : announceLater}</dd></div>
        <div><dt>Подведение итогов</dt><dd>{giveaway.drawAt ? formatMoscowDateTime(giveaway.drawAt) : announceLater}</dd></div>
      </dl>
      <p className="contest-info-rules"><Link to="/rozygrysh/pravila">Правила конкурса</Link></p>
    </section>
    {entry ? <ContestNextStep entry={entry} onStartOver={startOver} />
      : giveaway.networks.length === 0 ? <p className="contest-unavailable" role="status">Приём заявок временно недоступен — следите за новостями в <a href={giveawayChannelUrl} target="_blank" rel="noreferrer">Telegram-канале «Изнанки»</a>.</p>
        : <ContestForm networks={giveaway.networks} onEntered={entered} />}
  </div>;
}

function ContestNextStep({ entry, onStartOver }: { entry: GiveawayEntryResponse; onStartOver: () => void }) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const label = giveawayNetworkLabels[entry.network];
  useEffect(() => { headingRef.current?.focus({ preventScroll: true }); }, [entry]);
  return <section className="contest-step" aria-labelledby="contest-step-title">
    <h3 id="contest-step-title" ref={headingRef} tabIndex={-1}>Осталось подписаться</h3>
    {entry.network === 'max' ? <>
      <ol>
        <li>Подпишитесь на канал «Изнанки» в MAX.</li>
        <li>Откройте бота в MAX и нажмите «📱 Поделиться номером» — мы найдём вашу заявку по телефону, который вы указали, и бот проверит подписку.</li>
      </ol>
      <p>Нужно, чтобы вас можно было найти по номеру телефона. Оставайтесь подписаны до подведения итогов — подписку проверим ещё раз в момент выбора победителя, и участвуют только подписанные в этот момент.</p>
    </> : <>
      <ol>
        <li>Подпишитесь на канал «Изнанки» в {label}.</li>
        <li>Нажмите «Подтвердить участие в боте» в том аккаунте {label}, ник которого вы указали, — бот проверит подписку.</li>
      </ol>
      <p>Это нужно, чтобы мы узнали ваш аккаунт: по нику проверить подписку нельзя. Оставайтесь подписаны до подведения итогов — подписку проверим ещё раз в момент выбора победителя, и участвуют только подписанные в этот момент.</p>
    </>}
    <div className="contest-step-actions">
      <a className="button button--ghost" href={entry.channelUrl} target="_blank" rel="noreferrer">Подписаться на канал</a>
      <a className="button button--primary" href={entry.botUrl} target="_blank" rel="noreferrer">{entry.network === 'max' ? 'Открыть бота в MAX' : 'Подтвердить участие в боте'}</a>
    </div>
    <button type="button" className="text-button" onClick={onStartOver}>Отправить другую заявку</button>
  </section>;
}

type FieldErrors = Record<string, string>;

function ContestForm({ networks, onEntered }: { networks: GiveawayPublicNetwork[]; onEntered: (entry: GiveawayEntryResponse) => void }) {
  const [network, setNetwork] = useState<GiveawayNetwork | null>(networks.length === 1 ? networks[0].network : null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [sending, setSending] = useState(false);
  const [closed, setClosed] = useState(false);
  const [message, setMessage] = useState('');
  // 409 ALREADY_ENTERED for MAX: the entry is finished in the bot by sharing the phone, no token needed.
  const [alreadyBotUrl, setAlreadyBotUrl] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => { formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(); }, [errors]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (sending) return;
    const data = new FormData(event.currentTarget);
    // The tab's first-touch `utm_source/utm_content` tells the admin which network and community brought the entry.
    const ref = firstTouchRef();
    const parsed = giveawayEntrySchema.safeParse({
      name: String(data.get('name') ?? ''), phone: String(data.get('phone') ?? ''), network: network ?? '', social: String(data.get('social') ?? ''),
      rulesAccepted: data.get('rulesAccepted') === 'on', privacyAccepted: data.get('privacyAccepted') === 'on',
      website: String(data.get('website') ?? ''), ref,
      ...(ref ? { source: sanitizeGiveawaySource([ref.source, ref.content].filter(Boolean).join('/')) } : {}),
    });
    if (!parsed.success) {
      const nextErrors: FieldErrors = {};
      for (const issue of parsed.error.issues) nextErrors[String(issue.path[0])] ??= issue.message;
      setMessage(''); setAlreadyBotUrl('');
      setErrors(nextErrors);
      return;
    }
    setErrors({}); setMessage(''); setAlreadyBotUrl(''); setSending(true);
    try {
      const response = await fetch('/api/giveaway/entries', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(parsed.data) });
      if (response.status === 201) {
        trackGoal('giveaway_entry');
        onEntered(await response.json() as GiveawayEntryResponse);
        return;
      }
      const body = await response.clone().json().catch(() => null) as (ApiError & Pick<GiveawayAlreadyEntered, 'botUrl'>) | null;
      if (body?.error.code === 'GIVEAWAY_CLOSED') { setMessage(body.error.message); setClosed(true); return; }
      if (body?.error.code === 'ALREADY_ENTERED') { setMessage(body.error.message); setAlreadyBotUrl(body.botUrl ?? ''); return; }
      const nextMessage = await apiErrorMessage(response, 'Не удалось отправить заявку');
      setMessage(nextMessage);
      if (body?.error.fields) setErrors(body.error.fields);
    } catch {
      setMessage('Не удалось отправить заявку. Проверьте соединение и повторите отправку — данные сохранены в форме.');
    } finally {
      setSending(false);
    }
  }

  if (closed) return <p className="contest-unavailable" role="status">{message || 'Приём заявок закрыт.'} Итоги опубликуем в <a href={giveawayChannelUrl} target="_blank" rel="noreferrer">Telegram-канале «Изнанки»</a>.</p>;

  const describedBy = (name: string) => errors[name] ? `contest-${name}-error` : undefined;
  const fieldError = (name: string) => errors[name] && <small className="field-error" id={`contest-${name}-error`}>{errors[name]}</small>;
  return <form ref={formRef} className="lead-form contest-form" onSubmit={submit} noValidate aria-busy={sending}>
    <fieldset className="booking-fields" disabled={sending}><legend className="booking-sr-only">Заявка на участие в конкурсе</legend>
      <div className="form-grid">
        <label>Имя<input name="name" autoComplete="name" required maxLength={giveawayLimits.name} aria-invalid={Boolean(errors.name)} aria-describedby={describedBy('name')} />{fieldError('name')}</label>
        <label>Телефон<input name="phone" type="tel" inputMode="tel" autoComplete="tel" required maxLength={20} placeholder="+7 или 8…" aria-invalid={Boolean(errors.phone)} aria-describedby={describedBy('phone')} />{fieldError('phone')}</label>
        <fieldset className="booking-format full"><legend>Где вы есть?</legend>
          <div className="booking-format-options">
            {networks.map((item) => <label key={item.network}><input type="radio" name="network" value={item.network} required checked={network === item.network} onChange={() => setNetwork(item.network)} aria-invalid={Boolean(errors.network)} aria-describedby={describedBy('network')} /><span>{giveawayNetworkLabels[item.network]}</span></label>)}
          </div>
          {fieldError('network')}
          {network === 'max' && <p className="booking-media-note">Нужно, чтобы вас можно было найти по номеру телефона. В боте MAX вы поделитесь номером, и мы найдём по нему вашу заявку.</p>}
        </fieldset>
        {network !== 'max' && <label className="full">Ссылка или ник<input name="social" autoComplete="off" autoCapitalize="none" spellCheck={false} required maxLength={giveawayLimits.social} placeholder={network ? socialPlaceholders[network] : 'Сначала выберите сеть'} aria-invalid={Boolean(errors.social)} aria-describedby={describedBy('social')} />{fieldError('social')}</label>}
        <label className="honeypot" aria-hidden="true">Сайт<input name="website" tabIndex={-1} autoComplete="off" /></label>
        <fieldset className="booking-consents full"><legend className="booking-sr-only">Согласия</legend>
          <label className="check"><input name="rulesAccepted" type="checkbox" required aria-invalid={Boolean(errors.rulesAccepted)} aria-describedby={describedBy('rulesAccepted')} /><span>С <a href="/rozygrysh/pravila" target="_blank" rel="noopener">правилами конкурса</a> ознакомлен</span></label>
          {fieldError('rulesAccepted')}
          <label className="check"><input name="privacyAccepted" type="checkbox" required aria-invalid={Boolean(errors.privacyAccepted)} aria-describedby={describedBy('privacyAccepted')} /><span>Согласен на <a href="/rozygrysh/pravila#personal-data" target="_blank" rel="noopener">обработку персональных данных</a> для проведения конкурса</span></label>
          {fieldError('privacyAccepted')}
        </fieldset>
      </div>
      <button className="button button--primary" type="submit">{sending ? 'Отправляем…' : 'Участвовать в конкурсе'}</button>
    </fieldset>
    {message && <p className="form-error" role="alert">{message}</p>}
    {alreadyBotUrl && <a className="button button--primary contest-already-bot" href={alreadyBotUrl} target="_blank" rel="noreferrer">Открыть бота в MAX</a>}
  </form>;
}
